import { logger } from '@/utils';
import { useCallback } from 'react';

interface PageView {
  // Hostname of server
  hostname: string;
  // Browser language
  language: string;
  // Page referrer
  referrer: string;
  // Screen dimensions (eg. 1920x1080)
  screen: string;
  // Page title
  title: string;
  // Page url
  url: string;
  // Website ID (required)
  website: string;
}

type EventName = string;
type EventData = Record<string, string | number>;
type SessionData = Record<string, unknown>;

interface Umami {
  pageView(data?: Partial<PageView>): void;
  event(name: EventName, data?: EventData): void;
  identify(session_data: SessionData): void;
}

interface _Umami {
  track(data?: Partial<PageView>): void;
  track(callback: (props: PageView) => PageView): void;
  track(name: EventName, data?: EventData): void;
  identify(session_data: SessionData): void;
}

// https://umami.is/docs/tracker-functions
export default function useUmami(): Umami {
  const windowUmami = useCallback(() => {
    if (typeof (window as any).umami === 'undefined') {
      logger.debug('UmamiProvider not found');
      return;
    }

    return (window as any).umami as _Umami;
  }, []);

  const pageView = useCallback(
    (data?: Partial<PageView>) => {
      const umamiInstance = windowUmami();

      umamiInstance?.track((props: PageView) => ({
        ...props,
        ...(data && { ...data }),
      }));
    },
    [windowUmami],
  );

  const event = useCallback(
    (name: EventName, data?: EventData) => {
      const umamiInstance = windowUmami();

      umamiInstance?.track(name, { ...(data && { ...data }) });
    },
    [windowUmami],
  );

  const identify = useCallback(
    (session_data: SessionData) => {
      const umamiInstance = windowUmami();

      umamiInstance?.identify({
        ...(session_data && { ...session_data }),
      });
    },
    [windowUmami],
  );

  return { pageView, event, identify };
}
